import React from "react";
import {
  Chip,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { DeleteOutline, ExpandLess, ExpandMore } from "@mui/icons-material";
import { BuilderQuestion, readableIdentity } from "./form-builder-utils";
import { getQuestionTypeIcon } from "./form-builder-shared";

const chipSx = { verticalAlign: "middle" as const };

const QuestionTypeAndChildrenChips: React.FC<{
  questionType: string;
  childrenKeysLength: number;
}> = ({ questionType, childrenKeysLength }) => (
  <>
    <Chip
      size="small"
      variant="outlined"
      icon={getQuestionTypeIcon(questionType)}
      label={`T: ${questionType}`}
      sx={{ ...chipSx, ml: 0.75 }}
    />
    {childrenKeysLength > 0 && (
      <Chip
        size="small"
        variant="outlined"
        label={`children: ${childrenKeysLength}`}
        sx={{ ...chipSx, ml: 0.5 }}
      />
    )}
  </>
);

type Props = {
  question: BuilderQuestion;
  questionKey: string;
  questionSummary: string;
  questionType: string;
  isExpanded: boolean;
  onToggleExpanded: (questionId: string) => void;
  onDeleteQuestion: (questionId: string, questionKey: string) => void;
};

const FormBuilderQuestionCardHeader: React.FC<Props> = ({
  question,
  questionKey,
  questionSummary,
  questionType,
  isExpanded,
  onToggleExpanded,
  onDeleteQuestion,
}) => (
  <Grid container spacing={1}>
    <Grid size={{ xs: 12, md: 10 }}>
      <Grid container spacing={1}>
        <Grid size={{ xs: 12 }}>
          <IconButton
            aria-label={isExpanded ? "Collapse question" : "Expand question"}
            onClick={() => onToggleExpanded(question.draftId)}
            size="small"
          >
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
          <Typography variant="body2" component="span" fontWeight={700}>
            {questionSummary || "(untitled question)"}
          </Typography>
          {question.required && (
            <Typography component="span" color="primary.main" fontWeight={700} sx={{ ml: 0.25 }}>
              *
            </Typography>
          )}
          <QuestionTypeAndChildrenChips
            questionType={questionType}
            childrenKeysLength={question.childrenKeys.length}
          />
          <Typography component="span" variant="caption" color="text.secondary">
            {` ${readableIdentity(questionKey)}`}
            {question.alias.trim() ? ` | alias: ${question.alias.trim()}` : ""}
          </Typography>
        </Grid>
      </Grid>
    </Grid>
    <Grid size={{ xs: 12, md: 2 }} display="flex" justifyContent="flex-end" alignItems="center">
      <IconButton
        aria-label="Delete question"
        onClick={() => onDeleteQuestion(question.draftId, questionKey)}
      >
        <DeleteOutline />
      </IconButton>
    </Grid>
  </Grid>
);

export default FormBuilderQuestionCardHeader;
